/**
 * MyFilesPanel — staff-portal file management surface, reusable as a
 * standalone page or as a tab inside the My Profile detail page.
 *
 * Mirrors [admin/employees/[id]/components/EmployeeFilesTab.tsx]:
 *  - Table / Grid view modes via viewModeStore (`hr.my.files`)
 *  - File-type icons + extension-aware colours
 *  - Type + Category badges resolved from the staff-portal lookups
 *  - Click → preview via UniversalFileViewer
 *  - Add via Drawer (mirrors AddEmployeeFileDrawer for staff context)
 *  - Edit metadata (name / type / category / description / expiry) via Drawer
 *
 * HROnly files are filtered out server-side. Uploads always set HROnly=false.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Stack,
  Text,
  Group,
  Badge,
  Card,
  ThemeIcon,
  Button,
  Tooltip,
  SegmentedControl,
  SimpleGrid,
  Alert,
  Loader,
  Select,
  TextInput,
  Textarea,
} from '@/components/ui';
import { DatePickerInput } from '@mantine/dates';
import { Drawer } from '@/components/ui/overlays';
import ActionIcon from '@/components/ui/ActionIcon';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import {
  IconPlus, IconFile, IconLayoutGrid, IconLayoutList, IconEye, IconEdit, IconTrash, IconAlertCircle, IconInfoCircle,
} from '@tabler/icons-react';
import {
  useStaffPortalFileService,
  useStaffPortalLookupsService,
  type EmployeeFile,
  type EmployeeFileType,
  type EmployeeFileCategory,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import { useViewMode } from '@/stores/viewModeStore';
import { hrKeys } from '../../utils/queryKeys';
import { formatDate, lookupOptions, getFileIcon } from '../../utils/hrHelpers';
import { HR_FILE_DOMAINS } from '../../utils/hrConstants';
import { FileUploader } from '@/components/ccms-components/file-management/components/FileUploader';
import type { FileInfo } from '@/components/ccms-components/file-management/types';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer/UniversalFileViewer';
import type { ViewerFile } from '@/components/ccms-components/file-management/types';

type FilesViewMode = 'list' | 'grid';

const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/*',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

interface Props {
  /** Render compact: drop the toolbar Paper + intro text — useful when
      embedded as a tab inside the My Profile page. */
  compact?: boolean;
  /** Help page key of the host page: adds "{key}.files-toolbar", "{key}.add-file" and "{key}.files-list" tour anchors. */
  helpIdPrefix?: string;
}

/** Renders a file's expiry: a date (red once past), or "N/A" when unset. */
function ExpiryText({ expiryDate, size = 'sm' }: { expiryDate?: string | null; size?: 'xs' | 'sm' }) {
  if (!expiryDate) return <Text size="xs" c="dimmed">N/A</Text>;
  const past = dayjs(expiryDate).isBefore(dayjs(), 'day');
  return <Text size={size} c={past ? 'red' : undefined}>{formatDate(expiryDate)}</Text>;
}

export function MyFilesPanel({ compact = false, helpIdPrefix }: Props) {
  const hid = (part: string) => (helpIdPrefix ? `${helpIdPrefix}.${part}` : undefined);
  const queryClient = useQueryClient();
  const fileService = useStaffPortalFileService();
  const lookupsService = useStaffPortalLookupsService();

  const [viewMode, setViewMode] = useViewMode<FilesViewMode>('hr.my.files', 'list');

  // Add-file drawer state.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [typeID, setTypeID] = useState<string | null>(null);
  const [categoryID, setCategoryID] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);

  // Edit-file drawer state.
  const [editFile, setEditFile] = useState<EmployeeFile | null>(null);
  const [editTypeID, setEditTypeID] = useState<string | null>(null);
  const [editCategoryID, setEditCategoryID] = useState<string | null>(null);
  const [editFileName, setEditFileName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editExpiry, setEditExpiry] = useState<Date | null>(null);

  // Viewer state.
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const { data: files = [], isLoading, isFetching, error } = useQuery({
    queryKey: hrKeys.myFiles(),
    queryFn: () => fileService.getMyFiles(),
  });

  const { data: types = [] } = useQuery({
    queryKey: hrKeys.fileTypes(),
    queryFn: () => lookupsService.fileTypes(),
  });
  const { data: categories = [] } = useQuery({
    queryKey: hrKeys.fileCategories(),
    queryFn: () => lookupsService.fileCategories(),
  });

  // EmployeeFile rows carry only typeID / categoryID — resolve the display
  // name + colour from the lookups for the table / grid badges.
  const typeById = useMemo(() => {
    const m = new Map<number, EmployeeFileType>();
    types.forEach((t) => m.set(t.typeID, t));
    return m;
  }, [types]);
  const categoryById = useMemo(() => {
    const m = new Map<number, EmployeeFileCategory>();
    categories.forEach((c) => m.set(c.categoryID, c));
    return m;
  }, [categories]);

  const sortedFiles = useMemo(() => {
    const arr = [...(files as any[])];
    arr.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());
    return arr.map((f) => ({ ...f, id: f.fileID }));
  }, [files]);

  const viewerFiles: ViewerFile[] = useMemo(
    () =>
      sortedFiles.map((f: any) => ({
        id: String(f.fileID),
        fileName: f.fileName ?? `file-${f.fileID}`,
        mimeType: f.mediaType ?? 'application/octet-stream',
        fileSize: 0,
        url: f.fileURL ?? undefined,
        uploadedAt: f.uploadDate,
      })),
    [sortedFiles],
  );

  const openViewer = (fileId: number) => {
    const idx = sortedFiles.findIndex((f: any) => f.fileID === fileId);
    if (idx >= 0) {
      setViewerIndex(idx);
      setViewerOpen(true);
    }
  };

  const openEdit = (f: EmployeeFile) => {
    setEditFile(f);
    setEditTypeID(f.typeID ? String(f.typeID) : null);
    setEditCategoryID(f.categoryID ? String(f.categoryID) : null);
    setEditFileName(f.fileName ?? '');
    setEditDescription(f.description ?? '');
    setEditExpiry(f.expiryDate ? new Date(f.expiryDate) : null);
  };

  const recordMutation = useMutation({
    mutationFn: (payload: { fileURL: string; fileName: string; mediaType?: string }) =>
      fileService.createFile({
        typeID: Number(typeID),
        categoryID: Number(categoryID),
        fileName: payload.fileName,
        fileURL: payload.fileURL,
        mediaType: payload.mediaType ?? null,
        description: description || null,
        keywords: null,
        // hasExpired derives from whether an expiry date was picked.
        hasExpired: !!expiryDate,
        expiryDate: expiryDate ? dayjs(expiryDate).format('YYYY-MM-DD') : null,
      }),
    onSuccess: () => {
      toast.success('Uploaded', 'File added to your HR record.');
      queryClient.invalidateQueries({ queryKey: hrKeys.myFiles() });
      setDescription('');
      setTypeID(null);
      setCategoryID(null);
      setExpiryDate(null);
      setDrawerOpen(false);
    },
    onError: (e: Error) =>
      toast.error('Save failed', e.message),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editFile) throw new Error('No file selected');
      return fileService.updateFile(editFile.fileID, {
        typeID: Number(editTypeID),
        categoryID: Number(editCategoryID),
        fileName: editFileName.trim() || (editFile.fileName ?? 'file'),
        description: editDescription || null,
        keywords: null,
        hasExpired: !!editExpiry,
        expiryDate: editExpiry ? dayjs(editExpiry).format('YYYY-MM-DD') : null,
      });
    },
    onSuccess: () => {
      toast.success('Saved', 'File details updated.');
      queryClient.invalidateQueries({ queryKey: hrKeys.myFiles() });
      setEditFile(null);
    },
    onError: (e: Error) =>
      toast.error('Update failed', e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => fileService.deleteFile(fileId),
    onSuccess: () => {
      toast.success('Deleted', 'File removed.');
      queryClient.invalidateQueries({ queryKey: hrKeys.myFiles() });
    },
    onError: (e: Error) =>
      toast.error('Delete failed', e.message),
  });

  const confirmDelete = (file: any) => {
    modal.delete({
      itemName: file.fileName ?? 'this file',
      onDelete: () => deleteMutation.mutate(file.fileID),
    });
  };

  const canUpload = !!typeID && !!categoryID;
  const canSaveEdit = !!editTypeID && !!editCategoryID && editFileName.trim().length > 0;

  const typeOptions = lookupOptions(types as any[], (t) => t.typeID, (t) => t.name ?? `Type ${t.typeID}`);
  const categoryOptions = lookupOptions(categories as any[], (c) => c.categoryID, (c) => c.name ?? `Category ${c.categoryID}`);

  const columns: DataTableColumn<any>[] = useMemo(() => [
    {
      key: 'fileName',
      label: 'File',
      sortable: true,
      width: 240,
      render: (_v, f) => {
        const { Icon, color } = getFileIcon({ mediaType: f.mediaType, fileName: f.fileName });
        return (
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size="md" variant="light" color={color}>
              <Icon size={16} />
            </ThemeIcon>
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text size="sm" fw={500} truncate maw={180}>{f.fileName ?? `File #${f.fileID}`}</Text>
              {f.description && <Text size="xs" c="dimmed" truncate maw={180}>{f.description}</Text>}
            </Stack>
          </Group>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      width: 150,
      render: (_v, f) => {
        const t = typeById.get(f.typeID);
        return t?.name ? (
          <Badge size="xs" variant="light"
            style={t.color ? { backgroundColor: t.color + '22', color: t.color } : undefined}>
            {t.name}
          </Badge>
        ) : <Text size="xs" c="dimmed">None</Text>;
      },
    },
    {
      key: 'category',
      label: 'Category',
      width: 200,
      render: (_v, f) => {
        const c = categoryById.get(f.categoryID);
        return c?.name ? (
          <Badge
            size="xs"
            variant="light"
            style={c.color ? { backgroundColor: c.color + '22', color: c.color } : undefined}
            // Wrap the label instead of truncating so the full category shows.
            styles={{
              root: { height: 'auto', paddingTop: 3, paddingBottom: 3 },
              label: { whiteSpace: 'normal', overflow: 'visible', lineHeight: 1.3 },
            }}
          >
            {c.name}
          </Badge>
        ) : <Text size="xs" c="dimmed">None</Text>;
      },
    },
    {
      key: 'uploadDate',
      label: 'Uploaded',
      sortable: true,
      width: 120,
      render: (v) => <Text size="sm">{formatDate(v as string | null)}</Text>,
    },
    {
      key: 'expiryDate',
      label: 'Expires',
      width: 120,
      render: (_v, f) => <ExpiryText expiryDate={f.expiryDate} />,
    },
    {
      key: 'actions',
      label: '',
      width: 140,
      render: (_v, f) => (
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <Tooltip label="Preview">
            <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); openViewer(f.fileID); }}>
              <IconEye size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Edit">
            <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); openEdit(f); }}>
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete">
            <ActionIcon variant="subtle" color="red" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); confirmDelete(f); }}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ),
    },
  ], [openViewer, typeById, categoryById]);

  const renderGrid = () => (
    <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 4 }}>
      {sortedFiles.map((f: any) => {
        const { Icon, color } = getFileIcon({ mediaType: f.mediaType, fileName: f.fileName });
        const fileType = typeById.get(f.typeID);
        const fileCategory = categoryById.get(f.categoryID);
        return (
          <Card key={f.fileID} withBorder padding="md" radius="md" style={{ cursor: 'pointer' }} onClick={() => openViewer(f.fileID)}>
            <Stack gap="sm">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <ThemeIcon size="lg" variant="light" color={color}>
                  <Icon size={22} />
                </ThemeIcon>
                <Group gap={4} wrap="wrap" justify="flex-end">
                  {fileType?.name && (
                    <Badge size="xs" variant="light"
                      style={fileType.color ? { backgroundColor: fileType.color + '22', color: fileType.color } : undefined}>
                      {fileType.name}
                    </Badge>
                  )}
                  {fileCategory?.name && (
                    <Badge size="xs" variant="light"
                      style={fileCategory.color ? { backgroundColor: fileCategory.color + '22', color: fileCategory.color } : undefined}>
                      {fileCategory.name}
                    </Badge>
                  )}
                </Group>
              </Group>
              <Stack gap={2}>
                <Text size="sm" fw={500} lineClamp={1}>{f.fileName ?? `File #${f.fileID}`}</Text>
                {f.description && <Text size="xs" c="dimmed" lineClamp={2}>{f.description}</Text>}
                <Text size="xs" c="dimmed">Uploaded {formatDate(f.uploadDate)}</Text>
                <Group gap={4}>
                  <Text size="xs" c="dimmed">Expires</Text>
                  <ExpiryText expiryDate={f.expiryDate} size="xs" />
                </Group>
              </Stack>
              <Group gap={4} justify="flex-end">
                <Tooltip label="Preview">
                  <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); openViewer(f.fileID); }}>
                    <IconEye size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Edit">
                  <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); openEdit(f); }}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete">
                  <ActionIcon variant="subtle" color="red" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); confirmDelete(f); }}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Stack>
          </Card>
        );
      })}
    </SimpleGrid>
  );

  const loading = isLoading || isFetching;

  return (
    <Stack gap={compact ? 'xs' : 'md'}>
      <Group justify="space-between" wrap="wrap">
        {compact ? (
          <Text size="sm" c="dimmed">
            Your personal HR files. HR-only files (e.g. payroll documents) aren't visible here.
          </Text>
        ) : null}
        <Group gap="sm" ml="auto" data-help-id={hid('files-toolbar')}>
          <Text size="sm" c="dimmed">
            {loading && sortedFiles.length === 0 ? 'Loading…' : `${sortedFiles.length} file${sortedFiles.length === 1 ? '' : 's'}`}
          </Text>
          <SegmentedControl
            value={viewMode}
            onChange={(v) => setViewMode(v as FilesViewMode)}
            size="xs"
            data={[
              { value: 'list', label: <Tooltip label="Table"><Group gap={0} px={4}><IconLayoutList size={14} /></Group></Tooltip> },
              { value: 'grid', label: <Tooltip label="Grid"><Group gap={0} px={4}><IconLayoutGrid size={14} /></Group></Tooltip> },
            ]}
          />
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setDrawerOpen(true)} data-help-id={hid('add-file')}>
            Add File
          </Button>
        </Group>
      </Group>

      {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>Failed to load your files.</Alert>}

      <div data-help-id={hid('files-list')}>
      {sortedFiles.length === 0 && !loading ? (
        <Card withBorder>
          <Stack align="center" py="xl" gap="sm">
            <IconFile size={32} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">You don't have any files yet.</Text>
            <Button variant="light" leftSection={<IconPlus size={14} />} onClick={() => setDrawerOpen(true)}>
              Add your first file
            </Button>
          </Stack>
        </Card>
      ) : viewMode === 'list' ? (
        <Card withBorder p={0}>
          <DataTable<any>
            columns={columns}
            data={sortedFiles}
            loading={loading}
            enableInternalSort
            onRowClick={(row) => openViewer(row.fileID)}
            emptyMessage="You don't have any files yet."
            striped
            highlightOnHover
          />
        </Card>
      ) : renderGrid()}
      </div>

      {/* Add-file drawer */}
      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={<Text fw={600}>Add a file</Text>}
        position="right"
        size="md"
        padding="md"
      >
        <Stack gap="md">
          <Select
            label="File Type"
            required
            placeholder="Choose…"
            data={typeOptions}
            value={typeID}
            onChange={setTypeID}
          />
          <Select
            label="Category"
            required
            placeholder="Choose…"
            data={categoryOptions}
            value={categoryID}
            onChange={setCategoryID}
          />
          <Textarea
            label="Description"
            placeholder="Optional. What is this file?"
            autosize
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
          <DatePickerInput
            label="Expiry date"
            description="Leave blank if the file doesn't expire."
            placeholder="No expiry"
            clearable
            value={expiryDate}
            onChange={setExpiryDate}
          />

          <Stack gap={4}>
            <Text size="sm" fw={500}>File</Text>
            <Text size="xs" c="dimmed">Pick a file type and category before uploading.</Text>
            <FileUploader
              domain={HR_FILE_DOMAINS.EMPLOYEE_FILE}
              recordId="my-files"
              disabled={!canUpload}
              multiple={false}
              maxSize={20 * 1024 * 1024}
              accept={ACCEPTED_MIME_TYPES}
              hideAcceptedTypesHint
              onUploadComplete={(uploaded: FileInfo[]) => {
                const f = uploaded[0];
                if (f?.url) {
                  recordMutation.mutate({
                    fileURL: f.url,
                    fileName: f.fileName ?? 'file',
                    mediaType: f.mimeType ?? undefined,
                  });
                }
              }}
              onUploadError={(e: Error) =>
                toast.error('Upload failed', e.message)
              }
            />
          </Stack>

          {recordMutation.isPending && (
            <Group gap="xs"><Loader size="xs" /><Text size="xs" c="dimmed">Saving…</Text></Group>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setDrawerOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* Edit-file drawer */}
      <Drawer
        opened={!!editFile}
        onClose={() => setEditFile(null)}
        title={<Text fw={600}>Edit file</Text>}
        position="right"
        size="md"
        padding="md"
      >
        <Stack gap="md">
          <Alert color="blue" alertVariant="light" type="info" icon={<IconInfoCircle size={16} />}>
            Update the file's details. To replace the file itself, delete it and upload a new one.
          </Alert>

          <TextInput
            label="File name"
            required
            value={editFileName}
            onChange={(e) => setEditFileName(e.currentTarget.value)}
          />
          <Select
            label="File Type"
            required
            placeholder="Choose…"
            data={typeOptions}
            value={editTypeID}
            onChange={setEditTypeID}
          />
          <Select
            label="Category"
            required
            placeholder="Choose…"
            data={categoryOptions}
            value={editCategoryID}
            onChange={setEditCategoryID}
          />
          <Textarea
            label="Description"
            placeholder="Optional. What is this file?"
            autosize
            minRows={2}
            value={editDescription}
            onChange={(e) => setEditDescription(e.currentTarget.value)}
          />
          <DatePickerInput
            label="Expiry date"
            description="Leave blank if the file doesn't expire."
            placeholder="No expiry"
            clearable
            value={editExpiry}
            onChange={setEditExpiry}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setEditFile(null)}>Cancel</Button>
            <Button
              onClick={() => updateMutation.mutate()}
              loading={updateMutation.isPending}
              disabled={!canSaveEdit}
            >
              Save changes
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <UniversalFileViewer
        files={viewerFiles}
        opened={viewerOpen}
        initialIndex={viewerIndex}
        onClose={() => setViewerOpen(false)}
        onIndexChange={setViewerIndex}
      />
    </Stack>
  );
}
